/**
 * Product dimension tab — unit toggle, selected-size highlight,
 * and package/drawer variant matching.
 */
class ProductDimensionTab extends HTMLElement {
  connectedCallback() {
    // sticky-product-info.js moves PDP blocks after load, which disconnects
    // and reconnects this element: bind its own listeners only once, and
    // only redo the document listener and live diagram on reconnect
    if (!this.initialised) {
      this.initialised = true;
      this.setUp();
    }

    document.addEventListener(
      'dimension:variant:changed',
      this.handleVariantChanged,
    );

    const liveDiagramEl = this.querySelector('[data-cylindo-diagram]');
    this.liveDiagram = liveDiagramEl
      ? new CylindoDimensionDiagram(liveDiagramEl, this)
      : null;
  }

  setUp() {
    this.switchBtn = this.querySelector('[data-unit-switch]');
    this.sizeOptionIndex = Number(this.dataset.sizeOptionIndex || 0);
    this.unit = this.dataset.unit === 'in' ? 'in' : 'cm';

    if (this.switchBtn) {
      this.switchBtn.addEventListener('click', () => {
        this.toggleUnit();
      });
    }

    this.querySelectorAll('[data-unit-title]').forEach((label) => {
      label.addEventListener('click', () => {
        const nextUnit = label.getAttribute('data-unit-title');
        if (nextUnit === 'cm' || nextUnit === 'in') {
          this.unit = nextUnit;
          this.applyUnit();
        }
      });
    });

    // Size Guide dropdowns show a fixed size with nothing extra to reveal,
    // so only the Dimensions tab links arrows and rows on hover or tap
    if (!this.dataset.fixedSize) this.bindRowHighlight();

    this.handleVariantChanged = this.handleVariantChanged.bind(this);
    this.addEventListener(
      'dimension:variant:changed',
      this.handleVariantChanged,
    );

    const initialTitle = this.dataset.variantTitle
      || this.querySelector('[data-variant-title]')?.textContent
      || '';
    const initialSize = this.dataset.selectedSize || '';
    this.showGroup(initialTitle);
    this.updateForOptions(initialTitle);
    if (initialSize) {
      this.updateSizeLabels(initialSize);
      this.highlightColumn(initialSize);
    }

    this.watchTableWidths();
    this.centreSelectedColumns('auto');
  }

  /**
   * On mobile the size table scrolls sideways. Glide each visible table so
   * the selected size column sits in the middle of the area beside the
   * sticky label column, with the sizes either side in view (same idea as
   * pdp-option-centre.js for the size cards). Tables that don't overflow
   * are left alone.
   * @param {ScrollBehavior} behavior
   */
  centreSelectedColumns(behavior) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelectorAll('.product-dimension-tab__table-wrap').forEach((wrap) => {
      if (!wrap.clientWidth || wrap.scrollWidth <= wrap.clientWidth) return;
      const selected = wrap.querySelector('thead .is-selected');
      if (!selected) return;

      const label = wrap.querySelector('thead th:first-child');
      const labelWidth = label && label !== selected ? label.getBoundingClientRect().width : 0;
      const wrapBox = wrap.getBoundingClientRect();
      const cellBox = selected.getBoundingClientRect();
      const visibleCentre = wrapBox.left + labelWidth + (wrapBox.width - labelWidth) / 2;
      const offset = cellBox.left + cellBox.width / 2 - visibleCentre;

      wrap.scrollBy({ left: offset, behavior: reduceMotion ? 'auto' : behavior });
    });
  }

  /**
   * Tables inside a closed tab or a hidden group have no width yet, so they
   * can't be scrolled. Re-centre a table whenever its width changes, which
   * covers the tab opening, a group being shown and rotating the phone.
   */
  watchTableWidths() {
    if (!('ResizeObserver' in window)) return;
    const widths = new WeakMap();

    this.tableResizeObserver = new ResizeObserver((entries) => {
      const changed = entries.some((entry) => {
        const width = Math.round(entry.contentRect.width);
        const before = widths.get(entry.target);
        widths.set(entry.target, width);
        return width > 0 && width !== before;
      });
      if (changed) this.centreSelectedColumns('auto');
    });

    this.querySelectorAll('.product-dimension-tab__table-wrap').forEach((wrap) => {
      this.tableResizeObserver.observe(wrap);
    });
  }

  /**
   * Hovering a table row or a diagram arrow highlights both; on touch,
   * a tap does the same until another row or arrow is tapped.
   */
  bindRowHighlight() {
    const getRow = (target) => target.closest?.('[data-dimension-row]');

    this.addEventListener('pointerover', (event) => {
      if (event.pointerType !== 'mouse') return;
      const row = getRow(event.target);
      if (row) this.highlightRow(row.dataset.dimensionRow);
    });
    this.addEventListener('pointerout', (event) => {
      if (event.pointerType !== 'mouse') return;
      const row = getRow(event.target);
      if (row && !row.contains(event.relatedTarget)) this.highlightRow('');
    });
    this.addEventListener('pointerdown', (event) => {
      this.lastPointerType = event.pointerType;
    });
    this.addEventListener('click', (event) => {
      if (this.lastPointerType === 'mouse') return;
      const row = getRow(event.target);
      if (!row) return;
      const id = row.dataset.dimensionRow;
      this.highlightRow(this.highlightedRow === id ? '' : id);
    });
  }

  /**
   * @param {string} id - Row letter, or '' to clear
   */
  highlightRow(id) {
    this.highlightedRow = id;
    this.querySelectorAll('[data-dimension-row]').forEach((el) => {
      el.classList.toggle('is-highlighted', Boolean(id) && el.dataset.dimensionRow === id);
    });
    this.querySelectorAll('[data-cylindo-diagram]').forEach((diagram) => {
      diagram.classList.toggle('has-highlight', Boolean(id));
    });
  }

  disconnectedCallback() {
    this.liveDiagram?.destroy();
    document.removeEventListener(
      'dimension:variant:changed',
      this.handleVariantChanged,
    );
  }

  /**
   * Switches the visible unit between centimetres and inches.
   */
  toggleUnit() {
    this.unit = this.unit === 'cm' ? 'in' : 'cm';
    this.applyUnit();
  }

  /**
   * Shows values for the active unit and updates the switch state.
   */
  applyUnit() {
    const isCm = this.unit === 'cm';

    if (this.switchBtn) {
      this.switchBtn.setAttribute(
        'aria-checked',
        isCm ? 'true' : 'false',
      );
    }

    this.querySelectorAll('[data-unit]').forEach((el) => {
      const show = el.getAttribute('data-unit') === this.unit;
      if (show) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });

    this.querySelectorAll('[data-unit-label]').forEach((el) => {
      el.textContent = this.unit;
    });

    this.querySelectorAll('[data-unit-title]').forEach((el) => {
      const isActive = el.getAttribute('data-unit-title') === this.unit;
      el.classList.toggle('is-active', isActive);
    });
  }

  /**
   * @param {CustomEvent} event
   */
  handleVariantChanged(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;

    // A tab locked to one size (a Size Guide dropdown) only follows the
    // other options, such as storage.
    if (this.dataset.fixedSize) {
      this.showGroup(variant.title || '');
      return;
    }

    const sizeValue = this.getSizeValue(variant);
    this.liveDiagram?.setSize(sizeValue);

    this.showGroup(variant.title || '');
    this.updateSizeLabels(sizeValue);
    this.highlightColumn(sizeValue);
    this.updateForOptions(variant.title || '');
    this.updateVariantTitles(variant);
    this.centreSelectedColumns('smooth');
  }

  /**
   * @param {object} variant
   * @returns {string}
   */
  getSizeValue(variant) {
    if (Array.isArray(variant.options)) {
      const fromOptions = variant.options[this.sizeOptionIndex];
      if (fromOptions) return fromOptions;
    }

    const key = 'option' + (this.sizeOptionIndex + 1);
    return variant[key] || variant.option1 || variant.title || '';
  }

  /**
   * Highlights the size column in every dimension group so hidden
   * tables stay in sync when the second option changes later.
   * @param {string} sizeValue
   */
  highlightColumn(sizeValue) {
    const size = String(sizeValue || '').toLowerCase();
    const groups = this.querySelectorAll('[data-dimension-group]');
    const scopes = groups.length ? groups : [this];

    scopes.forEach((scope) => {
      this.highlightScope(scope, size);
    });
  }

  /**
   * Highlights the column whose match or label best fits the size.
   * Longest match wins so "Small Double" is not treated as "Double".
   * @param {Element} scope
   * @param {string} size
   */
  highlightScope(scope, size) {
    const cells = scope.querySelectorAll('[data-match]');
    let bestMatch = '';
    let bestLen = 0;

    cells.forEach((cell) => {
      const match = (cell.dataset.match || '').toLowerCase();
      const label = (cell.dataset.label || '').toLowerCase();

      [match, label].forEach((candidate) => {
        if (!candidate || !size.includes(candidate)) return;
        if (candidate.length <= bestLen) return;
        bestLen = candidate.length;
        bestMatch = match || label;
      });
    });

    cells.forEach((cell) => {
      const match = (cell.dataset.match || '').toLowerCase();
      const isSelected = Boolean(bestMatch) && match === bestMatch;
      cell.classList.toggle('is-selected', isSelected);
    });
  }

  /**
   * Writes the selected size into grouped table headings.
   * @param {string} sizeValue
   */
  updateSizeLabels(sizeValue) {
    const size = String(sizeValue || '').trim();
    if (!size) return;

    this.querySelectorAll('[data-dimension-size]').forEach((el) => {
      el.textContent = size;
    });
  }

  /**
   * Shows the group whose match parts all appear in the variant title.
   * Longest total match wins; an empty match always qualifies.
   * Falls back to the first group when nothing matches.
   * @param {string} variantTitle
   */
  showGroup(variantTitle) {
    const groups = [...this.querySelectorAll('[data-dimension-group]')];
    if (groups.length < 2) return;

    const title = this.normalizeOption(variantTitle);
    let matched = null;
    let bestLen = -1;

    groups.forEach((group) => {
      const parts = (group.dataset.groupMatch || '')
        .split('||')
        .map((part) => this.normalizeOption(part))
        .filter(Boolean);
      const allPresent = parts.every((part) => title.includes(part));
      if (!allPresent) return;

      const totalLen = parts.reduce((sum, part) => sum + part.length, 0);
      if (totalLen <= bestLen) return;

      bestLen = totalLen;
      matched = group;
    });

    if (!matched) matched = groups[0];

    groups.forEach((group) => {
      group.hidden = group !== matched;
    });
  }

  /**
   * Decodes HTML entities used in metafield option keys.
   * @param {string} value
   * @returns {string}
   */
  normalizeOption(value) {
    return String(value || '')
      .replace(/&amp;/g, '&')
      .replace(/&#39;|&apos;|&#x27;/gi, "'")
      .replace(/&quot;|&#34;/g, '"')
      .toLowerCase()
      .trim();
  }

  /**
   * Shows option::value spans that match the current variant title.
   * @param {string} variantTitle
   */
  updateForOptions(variantTitle) {
    const title = this.normalizeOption(variantTitle);

    this.querySelectorAll('[data-for-option]').forEach((el) => {
      const match = this.normalizeOption(el.dataset.forOption);
      const show = Boolean(match) && title.includes(match);

      el.classList.toggle('js-hide-item', !show);
      if (show) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });

    this.updateOptionRows();
  }

  /**
   * Hides package/drawer rows that have no visible variant value.
   * Rows without option maps stay visible.
   */
  updateOptionRows() {
    this.querySelectorAll('[data-dimension-row]').forEach((row) => {
      const optionItems = row.querySelectorAll('[data-for-option]');

      if (optionItems.length === 0) {
        row.hidden = false;
        row.classList.remove('js-hide-item');
        return;
      }

      const hasVisible = [...optionItems].some((el) => {
        return !el.hidden && !el.classList.contains('js-hide-item');
      });

      row.hidden = !hasVisible;
      row.classList.toggle('js-hide-item', !hasVisible);
    });
  }

  /**
   * Updates package headings to the best matching option title.
   * @param {object} variant
   */
  updateVariantTitles(variant) {
    const titles = this.querySelectorAll('[data-variant-title]');
    if (titles.length === 0) return;

    const options = Array.isArray(variant.options) ? variant.options : [];
    const variantThreeOptions = variant.title || '';
    const variantTwoOptions = options.length >= 2
      ? `${options[0]} / ${options[1]}`
      : variantThreeOptions;
    const variantOneOption = options[0] || variantThreeOptions;

    const items = [...this.querySelectorAll('[data-for-option]')];
    const hasThree = items.some((item) => {
      return item.dataset.forOption === variantThreeOptions.toLowerCase();
    });
    const hasTwo = items.some((item) => {
      return item.dataset.forOption === variantTwoOptions.toLowerCase();
    });
    const newTitle = hasThree
      ? variantThreeOptions
      : hasTwo
        ? variantTwoOptions
        : variantOneOption;

    titles.forEach((title) => {
      title.textContent = newTitle;
    });
  }
}

/**
 * Live dimensions diagram built from Cylindo frames (head-on, side-on,
 * and open-lid for ottomans) in the selected fabric. Views, crop boxes
 * and arrows per Cylindo code come from cylindo-dimension-layouts.json.
 * Each arrow names the table row label it measures, so its letter is
 * read from this product's table; if any arrow has no matching row the
 * static diagram is kept instead. Follows the 360 viewer (cylindo:state)
 * for fabric and the variant size for the single or double code.
 */
class CylindoDimensionDiagram {
  static FRAME_SIZE = 1024;

  static layoutsPromise = null;

  /** ms the main 360 viewer gets before a visible diagram follows a change */
  static MAIN_VIEWER_HEAD_START = 600;

  /**
   * @param {string} url
   * @returns {Promise<object>}
   */
  static loadLayouts(url) {
    if (!CylindoDimensionDiagram.layoutsPromise) {
      CylindoDimensionDiagram.layoutsPromise = fetch(url)
        .then((response) => (response.ok ? response.json() : {}))
        .catch(() => ({}));
    }
    return CylindoDimensionDiagram.layoutsPromise;
  }

  /**
   * @param {HTMLElement} element
   * @param {ProductDimensionTab} tab
   */
  constructor(element, tab) {
    this.element = element;
    this.staticEl = tab.querySelector('[data-static-diagram]');
    this.codeSingle = element.dataset.codeSingle || '';
    this.codeDouble = element.dataset.codeDouble || '';
    try {
      this.rows = JSON.parse(element.dataset.rows || '[]');
    } catch (error) {
      this.rows = [];
    }
    this.layouts = {};
    this.features = {};
    this.size = tab.dataset.selectedSize || '';
    // Locked to one size: ignore the page's selected size, keep its fabric
    this.fixedSize = Boolean(tab.dataset.fixedSize);
    this.code = '';
    this.builtCode = '';
    this.requestId = 0;

    const state = window.comfyCylindoState;
    if (state) this.features = state.features || {};

    this.handleState = this.handleState.bind(this);
    document.addEventListener('cylindo:state', this.handleState);

    // Only fetch Cylindo frames while the diagram is on screen (the
    // Dimensions accordion when scrolled to, a Size Guide dropdown when
    // open), so the main 360 viewer always has the network to itself
    this.visible = false;
    this.pending = true;
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        this.visible = entry.isIntersecting && entry.intersectionRect.height > 0;
        if (this.visible && this.pending) this.render();
      },
      { rootMargin: '200px 0px' },
    );
    // Watch the whole tab: the diagram's own box is empty until it has
    // drawn, so it would never count as on screen
    this.observer.observe(tab);

    CylindoDimensionDiagram.loadLayouts(element.dataset.layoutsUrl).then(
      (layouts) => {
        this.layouts = layouts || {};
        const stateCode = window.comfyCylindoState?.code;
        this.code = this.layouts[stateCode] && !this.fixedSize
          ? stateCode
          : this.getCodeFromSize(this.size);
        this.render();
      },
    );
  }

  destroy() {
    // A frame request still in flight must not overwrite the replacement
    // diagram's images once this one is gone
    this.destroyed = true;
    document.removeEventListener('cylindo:state', this.handleState);
    this.observer?.disconnect();
    clearTimeout(this.renderTimer);
  }

  /**
   * @param {CustomEvent} event
   */
  handleState(event) {
    const state = event.detail || {};
    this.features = state.features || {};
    if (this.layouts[state.code] && !this.fixedSize) this.code = state.code;
    // A fabric or size change: let the main viewer's frames go first
    this.render(CylindoDimensionDiagram.MAIN_VIEWER_HEAD_START);
  }

  /**
   * @param {string} sizeValue
   */
  setSize(sizeValue) {
    this.size = sizeValue;
    const code = this.getCodeFromSize(sizeValue);
    if (code === this.code) return;
    this.code = code;
    this.render();
  }

  /**
   * Same rule as the 360 viewer: double/king sizes use the double code.
   * @param {string} sizeValue
   * @returns {string}
   */
  getCodeFromSize(sizeValue) {
    const size = (sizeValue || '').toLowerCase();
    const preferDouble = size.includes('double') || size.includes('king')
      || !size.includes('single');
    const order = preferDouble
      ? [this.codeDouble, this.codeSingle]
      : [this.codeSingle, this.codeDouble];
    return order.find((code) => code && this.layouts[code]) || '';
  }

  /**
   * Letter of the first table row whose label matches one of the
   * arrow's labels, in the arrow's order of preference.
   * @param {string[]} labels
   * @returns {string}
   */
  getLetter(labels) {
    for (const label of labels) {
      const row = this.rows.find((item) => {
        return String(item.label || '').trim().toLowerCase() === label;
      });
      if (row) return row.id;
    }
    return '';
  }

  /**
   * @param {object[]} views
   * @returns {boolean}
   */
  hasAllLetters(views) {
    return views.every((view) => {
      return view.a.every((arrow) => this.getLetter(arrow.r));
    });
  }

  /**
   * @param {'h' | 'v'} axis
   * @param {string} letter
   * @returns {HTMLElement}
   */
  createArrow(axis, letter) {
    const arrow = document.createElement('div');
    arrow.className = `product-dimension-tab__arrow product-dimension-tab__arrow--${axis}`;
    arrow.dataset.dimensionRow = letter;
    const letterEl = document.createElement('span');
    letterEl.className = 'product-dimension-tab__arrow-letter';
    letterEl.textContent = letter;
    arrow.append(
      Object.assign(document.createElement('span'), {
        className: 'product-dimension-tab__arrow-line',
      }),
      letterEl,
      Object.assign(document.createElement('span'), {
        className: 'product-dimension-tab__arrow-line',
      }),
    );
    return arrow;
  }

  /**
   * Builds the views for a code: crop box, image and arrows.
   * @param {object[]} views
   */
  build(views) {
    const full = CylindoDimensionDiagram.FRAME_SIZE;
    this.element.replaceChildren();
    this.element.style.setProperty(
      '--live-max-width',
      views.length > 2 ? '680px' : '520px',
    );

    this.viewImages = views.map((view) => {
      const [left, top, right, bottom] = view.b;
      const width = right - left;
      const height = bottom - top;

      const viewEl = document.createElement('div');
      viewEl.className = 'product-dimension-tab__live-view';
      viewEl.style.setProperty('--view-grow', String(width));

      const stage = document.createElement('div');
      stage.className = 'product-dimension-tab__live-stage';
      const frame = document.createElement('div');
      frame.className = 'product-dimension-tab__live-frame';
      frame.style.setProperty('--crop-ratio', `${width} / ${height}`);
      frame.style.setProperty('--crop-width', `${(full / width) * 100}%`);
      frame.style.setProperty('--crop-left', `${(-left / width) * 100}%`);
      frame.style.setProperty('--crop-top', `${(-top / height) * 100}%`);
      const img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      frame.append(img);
      stage.append(frame);
      viewEl.append(stage);

      view.a.forEach((spec) => {
        const arrow = this.createArrow(spec.a, this.getLetter(spec.r));
        if (spec.a === 'h') {
          viewEl.append(arrow);
          return;
        }
        arrow.style.top = `${(spec.t || 0) * 100}%`;
        if (spec.p === 'l' || spec.p === 'r') {
          const side = spec.p === 'l' ? 'left' : 'right';
          const column = spec.c || 0;
          arrow.classList.add(`product-dimension-tab__arrow--${side}`);
          arrow.style.setProperty('--arrow-column', String(column));
          viewEl.classList.add(`product-dimension-tab__live-view--gutter-${side}`);
          const columns = Number(viewEl.style.getPropertyValue(`--gutter-${side}-columns`) || 1);
          viewEl.style.setProperty(`--gutter-${side}-columns`, String(Math.max(columns, column + 1)));
        } else {
          arrow.classList.add('product-dimension-tab__arrow--inside');
          arrow.style.left = `${spec.p * 100}%`;
        }
        stage.append(arrow);
      });

      this.element.append(viewEl);
      return img;
    });
  }

  /**
   * Smallest Cylindo size that still fills the rendered frames at the
   * screen's pixel density, in 128px steps, capped at the 1024 master.
   * @param {object[]} views
   * @returns {number}
   */
  getRequestSize(views) {
    const full = CylindoDimensionDiagram.FRAME_SIZE;
    const maxWidth = views.length > 2 ? 680 : 520;
    const width = this.element.parentElement?.clientWidth || maxWidth;
    const displayWidth = Math.min(width, maxWidth);
    const totalCrop = views.reduce((sum, view) => sum + (view.b[2] - view.b[0]), 0);
    const needed = (displayWidth / totalCrop) * full * (window.devicePixelRatio || 1);
    return Math.min(full, Math.max(256, Math.ceil(needed / 128) * 128));
  }

  /**
   * @param {string} code
   * @param {object} view
   * @param {number} size
   * @returns {string}
   */
  getFrameUrl(code, view, size) {
    const features = { ...this.features };
    delete features['STORAGE OPTIONS'];
    if (view.s) features['STORAGE OPTIONS'] = view.s;

    const params = Object.keys(features)
      .filter((key) => features[key])
      .map((key) => `feature=${encodeURIComponent(key)}:${encodeURIComponent(features[key])}`);
    params.push(`size=${size}`, 'encoding=webp');

    const path = encodeURIComponent(code);
    return `https://content.cylindo.com/api/v2/8699/products/${path}/frames/${view.f}/${path}.webp?${params.join('&')}`;
  }

  /**
   * @param {string} src
   * @returns {Promise<void>}
   */
  preload(src) {
    return new Promise((resolve, reject) => {
      const probe = new Image();
      probe.onload = () => resolve();
      probe.onerror = () => reject(new Error(`Cylindo frame failed: ${src}`));
      probe.src = src;
    });
  }

  /**
   * Renders now if on screen, otherwise when it scrolls into view.
   * @param {number} [delay] - Wait before fetching, in ms
   */
  render(delay = 0) {
    clearTimeout(this.renderTimer);
    if (this.destroyed) return;
    if (!this.visible) {
      this.pending = true;
      return;
    }
    this.pending = false;
    if (delay > 0) {
      this.renderTimer = setTimeout(() => this.renderNow(), delay);
    } else {
      this.renderNow();
    }
  }

  renderNow() {
    if (this.destroyed) return;
    const code = this.code;
    const views = this.layouts[code];
    if (!views || !this.hasAllLetters(views)) {
      this.showFallback();
      return;
    }

    const requestId = ++this.requestId;
    const size = this.getRequestSize(views);
    const sources = views.map((view) => this.getFrameUrl(code, view, size));

    Promise.all(sources.map((src) => this.preload(src)))
      .then(() => {
        if (this.destroyed || requestId !== this.requestId) return;
        if (this.builtCode !== code) {
          this.build(views);
          this.builtCode = code;
        }
        this.viewImages.forEach((img, index) => {
          img.src = sources[index];
        });
        this.element.hidden = false;
        if (this.staticEl) this.staticEl.hidden = true;
      })
      .catch(() => {
        if (this.destroyed || requestId !== this.requestId) return;
        this.showFallback();
      });
  }

  showFallback() {
    this.element.hidden = true;
    if (this.staticEl) this.staticEl.hidden = false;
  }
}

if (!customElements.get('product-dimension-tab')) {
  customElements.define('product-dimension-tab', ProductDimensionTab);
}
